import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
} from '@mui/material';
import { useAppBar } from '../../providers/AppBarProvider';
import { fetchSell, formatCurrency, handleError, getDescription, getSubtotal } from '../../utils/lib';
import { useLocation, useNavigate } from 'react-router-dom';
import ViewPage from './ViewPage';
import { useSnackbar } from "../../providers/SnackBarProvider";
import { useConfig } from '../../providers/ConfigProvider';
import BottomSheet from '../../layouts/BottomSheet';
import axios from 'axios';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ListItemProductRemove from '../../layouts/ListItemProductRemove';
import InventoryIcon from '@mui/icons-material/Inventory';
import Decimal from 'decimal.js';
import { useTranslation } from 'react-i18next';

function ViewSell() {
  const location = useLocation();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const [id, setId] = useState(location.state.id);
  const [sell, setSell] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const {updateTitle} = useAppBar();
  const {config, isLoadingConfig} = useConfig();
  const { t } = useTranslation();
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const sell = await fetchSell(id);
        setSell(sell);
        updateTitle('', t('view.sellTitle'));
        // updateTitle('', `Venta ${sell.finance_types_receipt.short_denomination}${sell.sell_point_number}-${sell.receipt_number}`);
      } catch (error) {
        handleError(error, snackbar);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [t]);

  const [deleteDialog, setDeleteDialog] = useState({
    open: false
  });
  const [deletePaymentDialog, setDeletePaymentDialog] = useState({
    open: false,
    paymentId: null
  });

  const handleDeleteOpen = () => {
    setDeleteDialog({
      open: true
    });
  };

  const handleDeleteClose = () => {
    setDeleteDialog({
      open: false
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(
        `${import.meta.env.VITE_API_URL}sells?id=${sell.id}`,
        { withCredentials: true }
      );
      
      handleDeleteClose();
      navigate(-1);
    } catch (error) {
      handleError(error, snackbar);
    }
  };

  const handleDeletePaymentOpen = (paymentId) => {
    setDeletePaymentDialog({
      open: true,
      paymentId
    });
  };

  const handleDeletePaymentClose = () => {
    setDeletePaymentDialog({
      open: false,
      paymentId: null
    });
  };

  const handleDeletePaymentConfirm = async () => {
    try {
      await axios.delete(
        `${import.meta.env.VITE_API_URL}sells_payments?id=${deletePaymentDialog.paymentId}`,
        { withCredentials: true }
      );
      // Refresh the payments list
      const updatedSell = await fetchSell(id);
      setSell(updatedSell);
      handleDeletePaymentClose();
    } catch (error) {
      handleError(error, snackbar);
    }
  };

  const handlePdf = async () => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}sells?id=${sell.id}&pdf_receipt`,
        { withCredentials: true }
      );
      
      const base64Data = response.data;
      const binaryString = atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'application/pdf' });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = t('view.sellPdfFilename', { id: sell.id });
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      handleError(error, snackbar);
    }
  };

  if (isLoading || isLoadingConfig || !sell) {
    return <ViewPage isLoading={true} />;
  }

  return (
    <ViewPage 
      isLoading={isLoading || isLoadingConfig}
    >
      <Box sx={{ px: 1 }}>
        <Box sx={{ mb: 2 }}>
          <Typography>{t('view.sellFieldDate')} {new Date(sell.date).toLocaleString()}</Typography>
          {sell.owner && (
            <Typography>{t('view.sellFieldOwner')} {sell.owner.name}</Typography>
          )}
          {sell.pet && (
            <Typography>{t('view.sellFieldPet')} {sell.pet.name}</Typography>
          )}
          <Typography>{t('view.sellFieldUser')} {sell.user.name}</Typography>
          <Typography>{t('view.sellFieldTotal')} {formatCurrency(sell.total)}</Typography>
          {sell.balance > 0 && (
            <Typography>{t('view.sellDebtorBalance')} {formatCurrency(sell.balance)}</Typography>
          )}
        </Box>

        <Divider sx={{ marginY: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography variant="h6">{t('common.products')}</Typography>
          <Box>
            {sell.items?.map((item, index) => (
              <ListItemProductRemove
                key={index}
                product={item.product.name}
                description={getDescription(item)}
                subtotal={formatCurrency(
                  //getSubtotal(item.quantity, item.price, item.discount_surcharge)
                  new Decimal(item.subtotal).toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
                )}
                index={index}
                thumbUrl={item.product.thumb_url}
                avatarContent={<InventoryIcon sx={{ fontSize: 20 }} />}
              />
            ))}
          </Box>
        </Box>

        <Divider sx={{ marginY: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography variant="h6">{t('common.payments')}</Typography>
          <List>
            {sell.payments?.map((payment, index) => (
              <ListItem
                key={index}
                secondaryAction={
                  !payment.deleted && config.permissions.actions_permissions.sells_payments.delete && (
                    <IconButton
                      edge="end"
                      onClick={() => handleDeletePaymentOpen(payment.id)}
                      sx={{ width: "42px", height: "42px" }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )
                }
                sx={{ paddingRight: "56px" }}
              >
                <ListItemText
                  primary={`${new Date(payment.date).toLocaleDateString()}. ${payment.name}: ${formatCurrency(payment.amount)}${payment.deleted ? ' ' + t('view.deleted') : ''}`}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Box>

      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('view.sellDeleteTitle', { receipt: sell.receipt })}</DialogTitle>
        <DialogContent>
          {t('view.sellDeleteMessage')}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deletePaymentDialog.open}
        onClose={handleDeletePaymentClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('view.deletePaymentTitle')}</DialogTitle>
        <DialogContent>
          {t('view.deletePaymentMessage')}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeletePaymentClose}>{t('common.cancel')}</Button>
          <Button onClick={handleDeletePaymentConfirm} variant="contained" color="error">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <BottomSheet 
        zIndex={1001}
        buttonGroups={[
          {
            label: t('common.actions'), 
            buttons: [
              ...(config.permissions.actions_permissions.sells.delete ? [{
                label: t('common.delete'),
                action: handleDeleteOpen,
                icon: <DeleteIcon />
              }] : []),
              ...(config.permissions.actions_permissions.sells.read ? [{
                label: t('view.pdfReceipt'),
                action: handlePdf,
                icon: <PictureAsPdfIcon />
              }] : []),
            ].filter(Boolean)
          }
        ]}
      />
    </ViewPage>
  );
}

export default ViewSell;

